import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
// 2026-07-04 tools-wide overhaul (operator-approved), audit item 13 :
// CSRPopup was the most divergent page - no SEO component, a
// hand-rolled error div instead of the shared Alert, unlabeled
// inputs, ad-hoc textarea styling, and an imperative
// document.getElementById copy handler. This normalization pass
// brings it onto the shared components and idioms every other tool
// page uses. The unused KeyIcon / CheckCircleIcon imports were
// removed in the same pass (audit item 11, dead-code cleanup).
import Alert from "../components/Alert";
import ActionButton from "../components/ActionButton";
import SEO from "../components/SEO";
import api from "../utils/api";
import { downloadFile } from "../utils/helpers";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import { generateStandardFilename } from "../../shared/utils/fileHelpers";
import countries from "../data/countries";
import organizationUnits from "../data/organizationUnits";
import SecurityNotice from "../components/SecurityNotice";

/**
 * Universal CSR Popup Generator
 *
 * This standalone popup can be embedded anywhere via a simple link or button.
 * It generates CSR and Private Key, displays both, and provides download options.
 *
 * Usage:
 * - Direct link: https://tools.trustico.com/csr/popup
 * - Popup: window.open('https://tools.trustico.com/csr/popup', 'csr-popup', 'width=1000,height=800')
 * - Iframe: <iframe src="https://tools.trustico.com/csr/popup" />
 */
function CSRPopup() {
  const [formData, setFormData] = useState({
    certificateType: "SSL/TLS",
    commonName: "",
    organization: "",
    organizationUnit: "IT Department",
    country: "US",
    state: "",
    city: "",
    email: "",
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  // Transient per-field copy feedback keyed by field name - the same
  // state-driven "Copied!" shape as src/client/pages/CSRGenerator.jsx.
  // 2026-07-04 tools-wide overhaul (operator-approved), audit item 12.
  // Transient "Copied!" feedback via the shared success-gated hook
  // (see src/client/hooks/useCopyFeedback.js). History : the local
  // handler this replaced was itself the 2026-07-04 tools-wide
  // overhaul's fix (audit items 12 and 13) for an imperative
  // document.getElementById DOM write that bypassed React state
  // entirely.
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();

  // Check if we're in a popup or iframe
  const isEmbedded = window.parent !== window || window.opener;

  // Store cleanup functions
  const cleanupRef = React.useRef([]);

  // Listen for initialization data
  useEffect(() => {
    let mounted = true;

    const handleMessage = (event) => {
      if (!mounted) return; // Prevent updates if component unmounted

      // Accept messages from same origin or the referrer origin
      const isSameOrigin = event.origin === window.location.origin;
      const isReferrerOrigin =
        document.referrer && event.origin === new URL(document.referrer).origin;

      if (!isSameOrigin && !isReferrerOrigin) {
        return;
      }

      if (event.data && event.data.type === "init-csr-popup") {
        const { commonName, organization, email, certificateType } = event.data;
        setFormData((prev) => ({
          ...prev,
          commonName: commonName || prev.commonName,
          organization: organization || prev.organization,
          email: email || prev.email,
          certificateType: certificateType || prev.certificateType,
        }));
      }
    };

    window.addEventListener("message", handleMessage);
    cleanupRef.current.push(() =>
      window.removeEventListener("message", handleMessage)
    );

    // Tell parent/opener we're ready
    const target = window.opener || window.parent;
    if (target && target !== window) {
      // Use document.referrer as origin or fall back to current origin
      const targetOrigin = document.referrer
        ? new URL(document.referrer).origin
        : window.location.origin;
      target.postMessage({ type: "csr-popup-ready" }, targetOrigin);
    }

    return () => {
      mounted = false;
      cleanupRef.current.forEach((cleanup) => cleanup());
      cleanupRef.current = [];
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateCommonName = (value, type) => {
    if (type === "S/MIME") {
      // Validate as e-mail for S/MIME Certificates
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(value);
    } else {
      // Allow wildcard domains like *.example.com for SSL/TLS Certificates
      const domainRegex =
        /^(\*\.)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      return domainRegex.test(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (
      !formData.commonName ||
      !formData.organization ||
      !formData.city ||
      !formData.state ||
      !formData.email
    ) {
      setError("All fields marked with * are required.");
      return;
    }

    // Common Name validation
    if (!validateCommonName(formData.commonName, formData.certificateType)) {
      const errorMsg =
        formData.certificateType === "S/MIME"
          ? "This is not a valid e-mail address."
          : "This is not a valid domain name.";
      setError(errorMsg);
      return;
    }

    setLoading(true);
    setError(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    try {
      const response = await api.generateCSR({
        ...formData,
        certificateType: formData.certificateType,
        keyType: "RSA",
        keySize: 2048,
        digestAlgorithm: "SHA-256",
      });

      setResult(response.data);

      // Notify parent/opener if embedded
      const target = window.opener || window.parent;
      if (target && target !== window) {
        const targetOrigin = document.referrer
          ? new URL(document.referrer).origin
          : window.location.origin;
        target.postMessage(
          {
            type: "csr-generated",
            csr: response.data.csr,
            privateKey: response.data.privateKey,
            commonName: formData.commonName,
            certificateType: formData.certificateType,
          },
          targetOrigin
        );
      }
    } catch (err) {
      const errorMsg = err.message || "The CSR could not be generated.";
      setError(errorMsg);

      // Notify parent/opener of error
      const target = window.opener || window.parent;
      if (target && target !== window) {
        const targetOrigin = document.referrer
          ? new URL(document.referrer).origin
          : window.location.origin;
        target.postMessage(
          {
            type: "csr-error",
            error: errorMsg,
          },
          targetOrigin
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSR = () => {
    if (result && result.csr) {
      const commonName = result.commonName || formData.commonName;
      const filename = generateStandardFilename(commonName, "csr", "txt");
      downloadFile(result.csr, filename);
    }
  };

  const handleDownloadPrivateKey = () => {
    if (result && result.privateKey) {
      const commonName = result.commonName || formData.commonName;
      const filename = generateStandardFilename(
        commonName,
        "privatekey",
        "txt"
      );
      downloadFile(result.privateKey, filename);
    }
  };

  const handleClose = () => {
    if (window.opener) {
      window.close();
    } else if (isEmbedded) {
      // Notify parent to close
      const target = window.parent;
      if (target && target !== window) {
        const targetOrigin = document.referrer
          ? new URL(document.referrer).origin
          : window.location.origin;
        target.postMessage({ type: "close-csr-popup" }, targetOrigin);
      }
    }
  };

  const handleReset = () => {
    setFormData({
      certificateType: "SSL/TLS",
      commonName: "",
      organization: "",
      organizationUnit: "IT Department",
      country: "US",
      state: "",
      city: "",
      email: "",
    });
    setResult(null);
    setError(null);
    resetCopyStatus();
  };

  // 2026-07-05 operator directive (site-wide dark-mode adaptation) : the
  // root div is a full-viewport page background, so it takes the solid
  // dark:bg-gray-900 page treatment (matching the body / Layout.jsx root),
  // NOT the translucent inset-panel treatment.
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
      {/* 2026-07-04 tools-wide overhaul (operator-approved), audit
          item 13 : every page renders the shared SEO component. This
          page is an embeddable popup utility (window.open / iframe,
          see the usage JSDoc on CSRPopup above), so it carries a
          robots="noindex, nofollow" to keep the bare utility out of
          search results (the shared component renders robots
          unconditionally, so this reconciles the static tag rather
          than sitting beside it - two opposite robots metas until
          2026-08-28) ; the canonical URL is deliberately omitted
          because a noindexed page must not advertise itself as a
          canonical target - the indexable CSR tool is /csr
          (src/client/pages/CSRGenerator.jsx). */}
      <SEO
        title="CSR Generator Popup"
        description="Embeddable popup utility that generates a Certificate Signing Request (CSR) and private key for SSL/TLS or S/MIME Certificates."
        robots="noindex, nofollow"
      />
      <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            {/* 2026-07-04 operator consistency directive : the page
                title is h1 text-3xl font-bold on every page (the
                CSRGenerator.jsx reference size) ; this popup was the
                one page rendering its h1 at text-2xl. */}
            <h1 className="text-3xl font-bold text-heading">
              {formData.certificateType === "S/MIME"
                ? "S/MIME Certificate"
                : "SSL Certificate"}{" "}
              CSR Generator
            </h1>
            {isEmbedded && (
              <button
                onClick={handleClose}
                className="text-muted hover:text-body"
                title="Close"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            )}
          </div>

          <p className="text-body mt-4">
            Fill in the information below to generate a Certificate Signing
            Request (CSR) and private key for your{" "}
            {formData.certificateType === "S/MIME"
              ? "S/MIME Certificate"
              : "SSL Certificate"}
            . Fields marked with an asterisk (*) are required.
          </p>

          {/* mt-6 because this is the ONE page rendering the notice
              inside its header block rather than after a mb-8 wrapper.
              Nothing here supplies a top margin - .alert carries only
              mb-4, SecurityNotice's default is mb-6, the parent has no
              space-y, and preflight zeroes the paragraph's bottom
              margin - so the box butted flush against the prose above
              it. BOTH halves are passed because className REPLACES the
              default rather than adding to it : mt-6 alone would drop
              mb-6 and leave the box on .alert's own mb-4. */}
          <SecurityNotice className="mt-6 mb-6" />
        </div>

        <div className="p-6">
          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* 2026-07-04 tools-wide overhaul (operator-approved),
                  audit item 13 : every label is paired to its input
                  via htmlFor/id, matching the accessible form markup
                  in src/client/pages/CSRGenerator.jsx.

                  2026-07-04 operator consistency directive : the HTML
                  `required` attributes were dropped from this form
                  because the disabled gate on the submit button blocks
                  empty click-submits and the handleSubmit guard covers
                  direct form submission with the standard error
                  sentence ; the reference page (CSRGenerator.jsx) carries
                  no `required` attributes either. */}
              <div>
                <label htmlFor="certificateType" className="label">
                  Certificate Type
                </label>
                <select
                  id="certificateType"
                  name="certificateType"
                  value={formData.certificateType}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="SSL/TLS">SSL/TLS Certificate</option>
                  <option value="S/MIME">S/MIME Certificate (E-Mail)</option>
                </select>
              </div>

              <div>
                <label htmlFor="commonName" className="label">
                  Common Name *
                </label>
                <input
                  type="text"
                  id="commonName"
                  name="commonName"
                  value={formData.commonName}
                  onChange={handleChange}
                  placeholder={
                    formData.certificateType === "S/MIME"
                      ? "user@example.com"
                      : "www.example.com or *.example.com"
                  }
                  className="input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="organization" className="label">
                    Organization *
                  </label>
                  <input
                    type="text"
                    id="organization"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    placeholder="Acme Inc."
                    className="input"
                  />
                </div>

                <div>
                  <label htmlFor="organizationUnit" className="label">
                    Organization Unit
                  </label>
                  <select
                    id="organizationUnit"
                    name="organizationUnit"
                    value={formData.organizationUnit}
                    onChange={handleChange}
                    className="input"
                  >
                    {organizationUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="label">
                    City *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="San Francisco"
                    className="input"
                  />
                </div>

                <div>
                  <label htmlFor="state" className="label">
                    State/Province *
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="California"
                    className="input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="country" className="label">
                    Country *
                  </label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="input"
                  >
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="email" className="label">
                    Administrative E-Mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="admin@example.com"
                    className="input"
                  />
                </div>
              </div>

              {/* 2026-07-04 tools-wide overhaul (operator-approved),
                  audit item 13 : errors render through the shared
                  Alert component instead of the previous hand-rolled
                  red div, matching every other tool page. The message
                  text is a plain sentence (operator ruling 2026-08-27 :
                  UPPERCASE is for the logs, never for what a customer
                  reads). */}
              {error && <Alert type="error">{error}</Alert>}

              <div className="flex justify-center pt-4">
                {/* 2026-07-04 operator consistency directive : the
                    primary action is disabled until every required
                    field is non-empty (Country and Organization Unit
                    are selects with defaults, so only the free-text
                    fields gate), and its label is the standard concise
                    "Generate CSR" used by the sibling CSRGenerator
                    page rather than the longer type-specific wording.
                    handleSubmit keeps its required-field guard as the
                    direct-form-submission backstop and its Common Name
                    FORMAT validation on click. */}
                <ActionButton
                  type="submit"
                  disabled={
                    loading ||
                    !formData.commonName.trim() ||
                    !formData.organization.trim() ||
                    !formData.city.trim() ||
                    !formData.state.trim() ||
                    !formData.email.trim()
                  }
                  loading={loading}
                  loadingText="Generating..."
                  icon={DocumentTextIcon}
                >
                  Generate CSR
                </ActionButton>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* CSR Section.
                  2026-07-04 operator consistency directive : these
                  result headings were size-less h4 elements ; they are
                  the section headings of the two result cards, so they
                  now carry the reference section treatment (text-lg
                  font-semibold) plus the explanatory sentence every
                  section heading requires. */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-heading">
                    Certificate Signing Request (CSR)
                  </h2>
                  {/*
                    NOT CopyValueButton, on purpose (operator decision,
                    2026-08-24, asked and answered directly). These are
                    SECTION-HEADER copies sitting beside a full-size
                    Download button ; the shared component is the
                    compact field-level one at text-xs, and using it
                    here would leave the row at two sizes. What the
                    shared component was actually needed for was its
                    interpolated accessible name, and that is taken
                    below without the size.

                    THE LABEL MUST FLIP WITH THE STATE. An aria-label
                    overrides text content when the accessible name is
                    computed, so a fixed "Copy Private Key" would hide
                    the "Copied!" confirmation from a screen reader
                    while a sighted customer sees it - the same
                    reasoning as the JSDoc on CopyValueButton.jsx.
                  */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleCopy(result.csr, "csr")}
                      aria-label={
                        copyStatus.csr
                          ? "Copied!"
                          : "Copy Certificate Signing Request (CSR)"
                      }
                      className="inline-flex items-center gap-1 px-3 py-1 border border-control rounded-sm hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      {copyStatus.csr ? "Copied!" : "Copy"}
                    </button>
                    <ActionButton
                      onClick={handleDownloadCSR}
                      icon={ArrowDownTrayIcon}
                      variant="outline"
                    >
                      Download CSR
                    </ActionButton>
                  </div>
                </div>
                {/* Explanatory sentence per the 2026-07-04 operator
                    consistency directive (see the CSR Section comment
                    above). */}
                <p className="text-body mb-4">
                  Submit this CSR to your Certificate Authority (CA) to obtain
                  your SSL Certificate.
                </p>
                {/* 2026-07-04 tools-wide overhaul (operator-approved),
                    audit item 13 : the shared textarea +
                    certificate-textarea classes replace the ad-hoc
                    inline utility string so PEM blocks render
                    identically across every tool page. The aria-label
                    stands in for a visible label because the section
                    heading above is a section heading, not a form label. */}
                <textarea
                  value={result.csr}
                  readOnly
                  aria-label="Certificate Signing Request (CSR)"
                  className="textarea certificate-textarea"
                  rows={8}
                  onClick={(e) => e.target.select()}
                />
              </div>

              {/* Private Key Section - same heading treatment as the
                  CSR Section above (2026-07-04 operator consistency
                  directive). */}
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-lg font-semibold text-heading">
                    Private Key
                  </h2>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleCopy(result.privateKey, "key")}
                      aria-label={
                        copyStatus.key ? "Copied!" : "Copy Private Key"
                      }
                      className="inline-flex items-center gap-1 px-3 py-1 border border-control rounded-sm hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <ClipboardDocumentIcon className="w-4 h-4" />
                      {copyStatus.key ? "Copied!" : "Copy"}
                    </button>
                    <ActionButton
                      onClick={handleDownloadPrivateKey}
                      icon={ArrowDownTrayIcon}
                      variant="outline"
                    >
                      {/* Label normalized from "Download Key" to match
                          the sibling "Download Private Key" button on
                          CSRGenerator.jsx (2026-07-04 operator
                          consistency directive). */}
                      Download Private Key
                    </ActionButton>
                  </div>
                </div>
                {/* Explanatory sentence per the 2026-07-04 operator
                    consistency directive (see the CSR Section comment
                    above). */}
                <p className="text-body mb-4">
                  Store this private key securely and never share it. Trustico®
                  does not retain a copy.
                </p>
                {/* Shared PEM textarea treatment - see the WHY comment
                    on the CSR textarea above (audit item 13). */}
                <textarea
                  value={result.privateKey}
                  readOnly
                  aria-label="Private Key"
                  className="textarea certificate-textarea"
                  rows={8}
                  onClick={(e) => e.target.select()}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-4 pt-4">
                <ActionButton
                  onClick={handleReset}
                  variant="outline"
                  icon={XMarkIcon}
                >
                  {/* 2026-07-04 operator consistency directive : this
                      button invokes handleReset, so it carries the
                      standard "Reset" label every tool page uses (the
                      previous "Generate Another CSR" wording was the
                      suite's only nonstandard reset label), and the
                      standard XMarkIcon reset icon instead of the
                      document icon. */}
                  Reset
                </ActionButton>
                {isEmbedded && (
                  <ActionButton
                    onClick={handleClose}
                    variant="outline"
                    icon={XMarkIcon}
                  >
                    Close
                  </ActionButton>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 text-center text-body">
          Powered by{" "}
          <a
            href="https://www.trustico.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-link hover:underline"
          >
            Trustico®
          </a>{" "}
          Online Security
        </div>
      </div>
    </div>
  );
}

export default CSRPopup;
